import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Rocket, DollarSign, Users, Dumbbell, Calendar, Lock, type LucideIcon } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import api from '../lib/api';

const ROLES = [
  { id: 'executive', label: 'Executive & Leader' },
  { id: 'entrepreneur', label: 'Entrepreneur' },
  { id: 'both', label: 'Both' },
  { id: 'other', label: 'Other' },
];

const TRACKS: { id: string; label: string; Icon: LucideIcon }[] = [
  { id: 'executive', label: 'Executive & Stakeholders', Icon: Building2 },
  { id: 'ventures', label: 'Ventures & Startups', Icon: Rocket },
  { id: 'finance', label: 'Personal Finance', Icon: DollarSign },
  { id: 'personal', label: 'Family & Personal', Icon: Users },
  { id: 'health', label: 'Health & Fitness', Icon: Dumbbell },
];

function Onboard() {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [tracks, setTracks] = useState<string[]>(TRACKS.map(t => t.id));
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuthStore();

  const toggleTrack = (id: string) => {
    setTracks(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const nextStep = () => {
    if (step < 3) setStep(step + 1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && step === 1 && name) {
      nextStep();
    }
  };

  const handleComplete = () => {
    console.log('Start AXIS clicked');
    const authStore = useAuthStore.getState();
    localStorage.setItem('axis_onboarded', 'true');
    localStorage.setItem('axis_user_name', name || 'User');

    if (authStore.user) {
      authStore.setUser({
        ...authStore.user,
        onboarding_complete: true,
      });
    }

    api.put('/auth/onboarding', {
      name: name || 'User',
      role: role || 'Executive',
      tracks: tracks || [],
      timezone: 'Asia/Dubai',
      language: 'en',
    }).catch((err) => console.error('[Onboard] saving profile failed:', err));

    api.put('/auth/onboarding/complete').catch((err) => console.error('[Onboard] completing onboarding failed:', err));

    window.location.href = '/today';
  };

  return (
    <div className="min-h-dvh bg-bg flex flex-col items-center justify-center px-4">
      <div className="flex gap-2 mb-2">
        {[1, 2, 3].map(s => (
          <div
            key={s}
            className={`w-2.5 h-2.5 rounded-full transition-colors ${
              s === step ? 'bg-gold' : 'bg-border'
            }`}
          />
        ))}
      </div>
      <p className="text-muted text-xs mb-8">Step {step} of 3</p>

      <div className="w-full max-w-md px-4">
          {step === 1 && (
            <div className="text-center" onKeyDown={handleKeyDown}>
              <h2 className="text-2xl text-text font-light mb-8">
                What should AXIS call you?
              </h2>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoFocus
                className="w-full bg-transparent border-b-2 border-border text-center text-2xl text-text placeholder-muted/50 py-3 focus:outline-none focus:border-gold transition-colors"
              />
              <div className="mt-10">
                <p className="text-muted text-sm mb-4">Your primary focus:</p>
                <div className="grid grid-cols-2 gap-3">
                  {ROLES.map(r => (
                    <button
                      key={r.id}
                      onClick={() => setRole(r.id)}
                      className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                        role === r.id
                          ? 'border-gold text-gold bg-gold/5'
                          : 'border-border text-muted hover:border-muted'
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={nextStep}
                disabled={!name}
                className="w-full mt-8 bg-gold text-bg font-semibold py-3 rounded-lg hover:bg-gold/90 transition-colors disabled:opacity-30"
              >
                Next →
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="text-center">
              <h2 className="text-2xl text-text font-light mb-2">
                What does AXIS need to track for you?
              </h2>
              <p className="text-muted text-sm mb-8">Select all that apply</p>
              <div className="space-y-3">
                {TRACKS.map(t => (
                  <button
                    key={t.id}
                    onClick={() => toggleTrack(t.id)}
                    className={`w-full flex items-center gap-4 px-5 py-4 rounded-xl border transition-all ${
                      tracks.includes(t.id)
                        ? 'border-l-4 border-l-gold border-t-border border-r-border border-b-border bg-gold/5'
                        : 'border-border hover:border-muted'
                    }`}
                  >
                    <t.Icon size={20} strokeWidth={1.8} className="text-text" />
                    <span className={`text-sm font-medium ${
                      tracks.includes(t.id) ? 'text-text' : 'text-muted'
                    }`}>
                      {t.label}
                    </span>
                    <span className="ml-auto">
                      {tracks.includes(t.id) && (
                        <svg className="w-5 h-5 text-gold" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </span>
                  </button>
                ))}
              </div>
              <button
                onClick={nextStep}
                className="w-full mt-8 bg-gold text-bg font-semibold py-3 rounded-lg hover:bg-gold/90 transition-colors"
              >
                Next →
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="text-center">
              <h2 className="text-2xl text-text font-light mb-2">Power up AXIS</h2>
              <p className="text-muted text-sm mb-8">Connect your tools — skip anything for later</p>
              <div className="space-y-4">
                <div className="bg-card rounded-xl border border-border p-5 text-left">
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar size={24} strokeWidth={1.8} className="text-text" />
                    <div>
                      <p className="text-text font-medium text-sm">Google Calendar</p>
                      <p className="text-muted text-xs">Sync your meetings and schedule</p>
                    </div>
                  </div>
                  <button className="w-full mt-3 border border-gold text-gold text-sm font-medium py-2 rounded-lg hover:bg-gold/5 transition-colors">
                    Connect →
                  </button>
                  <p className="text-muted text-xs text-center mt-2 hover:text-text cursor-pointer">Skip for now</p>
                </div>

                <div className="bg-card rounded-xl border border-border p-5 text-left">
                  <div className="flex items-center gap-3 mb-2">
                    <Dumbbell size={24} strokeWidth={1.8} className="text-text" />
                    <div>
                      <p className="text-text font-medium text-sm">WHOOP</p>
                      <p className="text-muted text-xs">Track recovery and optimize energy</p>
                    </div>
                  </div>
                  <button className="w-full mt-3 border border-gold text-gold text-sm font-medium py-2 rounded-lg hover:bg-gold/5 transition-colors">
                    Connect →
                  </button>
                  <p className="text-muted text-xs text-center mt-2 hover:text-text cursor-pointer">Skip for now</p>
                </div>

                <div className="bg-card rounded-xl border border-border/50 p-5 text-left opacity-50">
                  <div className="flex items-center gap-3">
                    <Lock size={24} strokeWidth={1.8} className="text-muted" />
                    <div>
                      <p className="text-muted font-medium text-sm">Gmail</p>
                      <p className="text-muted text-xs">Read email context and draft replies</p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handleComplete}
                disabled={loading}
                className="w-full mt-8 bg-gold text-bg font-semibold py-3.5 rounded-lg hover:bg-gold/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <span className="w-5 h-5 border-2 border-bg border-t-transparent rounded-full animate-spin"></span>
                ) : (
                  'Start AXIS →'
                )}
              </button>
              <p className="text-muted text-xs mt-3">You can connect anytime in Settings</p>
            </div>
          )}
      </div>
    </div>
  );
}

export default Onboard;
